import {
  Alignment,
  EventCallback,
  EventType,
  Fit,
  Layout,
  Event as RiveEvent,
  StateMachineInput,
  StateMachineInputType,
  useRive,
} from '@rive-app/react-canvas';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

const applyRiveValue = (
  input: StateMachineInput,
  value: number | boolean | undefined,
) => {
  switch (input.type) {
    case StateMachineInputType.Trigger:
      input.fire();
      break;
    case StateMachineInputType.Number:
      input.value = typeof value === 'number' ? value : 0;
      break;
    case StateMachineInputType.Boolean:
      input.value = typeof value === 'boolean' ? value : !input.value;
      break;
  }
};

/**
 * To trigger an animation event, use the `trigger` method.
 * const riveRef = useRef<RiveAnimationRef>(null);
 * const handleTrigger = (inputName: string) => riveRef.current?.trigger(inputName)
 */
export interface RiveAnimationRef {
  trigger: (name: string, value?: number | boolean) => void;
}

export interface RiveAnimationProps {
  src: string;
  height: string | number;
  width?: string | number;
  stateMachine?: string;
  initialInputValues?: Record<string, number | boolean>;
  onEvent?: (event: RiveEvent) => void;
}

export const RiveAnimation = forwardRef<RiveAnimationRef, RiveAnimationProps>(
  (props, ref) => {
    const riveInputs = useRef<StateMachineInput[] | undefined>(undefined);
    const [queuedTriggers, setQueuedTriggers] = useState<
      { name: string; value: number | boolean | undefined }[]
    >([]);

    const { RiveComponent, rive } = useRive({
      src: props.src,
      autoplay: props.initialInputValues === undefined, // rive animation cannot be playing when setting initial input values
      stateMachines: props.stateMachine ?? 'State Machine 1',
      layout: new Layout({
        alignment: Alignment.Center,
        fit: Fit.FitHeight,
      }),
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the Rive instance is ready and when triggers queue up. Callers pass initialInputValues and onEvent inline, so depending on them would re-run every render and reset the inputs.
    useEffect(() => {
      if (!rive) {
        return;
      }

      const inputs = rive?.stateMachineInputs(
        props.stateMachine ?? 'State Machine 1',
      );

      if (inputs) {
        if (props.initialInputValues !== undefined) {
          setInitialInputValues(inputs);
        }

        riveInputs.current = inputs;

        if (queuedTriggers.length > 0) {
          for (const queuedTrigger of queuedTriggers) {
            const input = inputs.find(
              (input) =>
                input.name.toLowerCase() === queuedTrigger.name.toLowerCase(),
            );

            input && applyRiveValue(input, queuedTrigger.value);
          }

          setQueuedTriggers([]);
        }
      }

      const handleRiveEvent: EventCallback = (event) => {
        props.onEvent?.(event);
      };

      rive.on(EventType.RiveEvent, handleRiveEvent);

      return () => {
        rive.off(EventType.RiveEvent, handleRiveEvent);
      };
    }, [rive, queuedTriggers]);

    const setInitialInputValues = (inputs: StateMachineInput[]) => {
      inputs.forEach((input) => {
        const initialValue = props.initialInputValues![input.name];

        if (initialValue !== undefined) {
          switch (input.type) {
            case StateMachineInputType.Number:
              input.value = Number(initialValue);
              break;
            case StateMachineInputType.Boolean:
              input.value = Boolean(initialValue);
              break;
            default:
              break;
          }
        }
      });

      // rive animation cannot be playing when setting initial input values
      if (rive && !rive.isPlaying) {
        rive.play();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        trigger: (name: string, value?: number | boolean) => {
          const input = riveInputs?.current?.find(
            (input) => input.name.toLowerCase() === name.toLowerCase(),
          );

          if (input === undefined) {
            setQueuedTriggers((prev) => {
              if (prev.find((t) => t.name === name && t.value === value)) {
                return prev;
              }

              return [
                ...prev,
                {
                  name,
                  value,
                },
              ];
            });
            return false;
          }

          applyRiveValue(input, value);

          return true;
        },
      };
    });

    return (
      <RiveComponent
        style={{
          width: props.width ?? '100%',
          height: props.height,
        }}
      />
    );
  },
);
